import React, { useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  TableWitPagination,
  SortHeader,
  PaginationContext,
  SortContext,
  SearchContext,
  Text,
  Badge,
} from "dash-ui";
import { AccountStatusesEnum } from "dash-common";

import { SaleTeamUser, useGetSaleTeamUsersQuery } from "gql/generated.graphql";
import DeliveryImage from "assets/images/png/Delivery.png";
import SearchingImage from "assets/images/png/Searching.png";
import { USER_STATUSES_BADGE_TYPE } from "pages/organizations/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/components/OrganizationUsersTable/OrganizationUsersTable.constants";

import SaleUsersTableRowActions from "./components/SaleUsersTableRowActions";

function ExternalSalesUsersTable() {
  const { id: saleTeamId = "" } = useParams();
  const { t } = useTranslation("tables", {
    keyPrefix: "organizationUsers",
  });
  const { t: tStatuses } = useTranslation("common");
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const { t: tPlaceholders } = useTranslation("common", {
    keyPrefix: "placeholders",
  });
  const { t: tForms } = useTranslation("forms", { keyPrefix: "labels" });
  const {
    setTotal,
    state: paginationState,
    resetPagination,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const searchText = searchSlug.length > 2 ? searchSlug : null;

  const {
    data: result,
    refetch,
    loading,
  } = useGetSaleTeamUsersQuery({
    variables: {
      filter: { searchText, saleTeamId },
      pagination: { skip, limit },
      sort: [sort],
    },
  });

  const { data, total } = useMemo(
    () => ({
      data: result?.getSaleTeamUsers?.collection || [],
      total: result?.getSaleTeamUsers?.totalCount || 0,
    }),
    [result]
  );

  // FIXME: re-check memo
  const columns = React.useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="profile.firstName" isDefault sortOrder={1}>
            {tLabels`name`}
          </SortHeader>
        ),
        accessor: "profile",
        Cell: ({
          value,
        }: {
          value: { firstName: string; lastName: string };
        }) => (
          <Text>
            {value.lastName
              ? `${value.firstName} ${value.lastName}`
              : value.firstName}
          </Text>
        ),
      },
      {
        Header: () => (
          <SortHeader accessor="account.email">{tForms`email`}</SortHeader>
        ),
        accessor: "account.email",
      },
      {
        Header: () => (
          <SortHeader
            accessor="role.type"
            reversed={true}
          >{tForms`role`}</SortHeader>
        ),
        accessor: "role.type",
        Cell: ({ value }: { value: string }) => (
          <Text>{tStatuses(`common:roles.${value}`)}</Text>
        ),
      },
      {
        Header: () => (
          <SortHeader accessor="account.status">{tLabels`status`}</SortHeader>
        ),
        accessor: "account.status",
        Cell: ({ value }: { value: AccountStatusesEnum }) => (
          <Badge
            title={tStatuses(`common:accountStatuses.${value}`)}
            type={USER_STATUSES_BADGE_TYPE[value]}
          />
        ),
      },
      {
        Header: () => <Text>{tLabels`actions`}</Text>,
        accessor: "_id",
        Cell: ({ row: { original } }: { row: { original: SaleTeamUser } }) => (
          <SaleUsersTableRowActions user={original} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tLabels]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSlug]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    refetch({
      filter: { searchText, saleTeamId },
      pagination: { skip, limit },
      sort: [sort],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, sort, searchSlug]);

  const labelToShow = !loading
    ? (searchText ? tPlaceholders`noSearchResults` : t`placeholder`) || ""
    : "";

  const imageToShow = !loading
    ? searchText
      ? SearchingImage
      : DeliveryImage
    : undefined;

  return (
    <TableWitPagination
      w="100%"
      tableProps={{
        data,
        columns,
        loading,
        maxHeight: "calc(100vh - 350px)",
        stickyHeader: true,
        placeholderProps: {
          label: labelToShow,
          image: imageToShow,
        },
      }}
      paginationProps={{
        label: t`paginationLabel`,
        previousButtonLabel: tLabels`previous`,
        nextButtonLabel: tLabels`next`,
      }}
    />
  );
}

export default ExternalSalesUsersTable;
